import { AxiosResponse } from "axios";

import type {
  AssigneeUpdateRequest,
  Assignment,
  FormQuestionUpsert,
  GetQuestionnaireFormReadReponse,
  GetQuestionnaireResponse,
  GetQuestionnairesResponse,
  QuestionnaireAnswerPostRequest,
  QuestionnaireRead,
  QuestionnaireRequest,
} from "@/features/risk-assessment";

import api from "../api";

export const QuestionnaireAPI = {
  // GET /api/react/questionnaires
  async getAll(
    moduleWorkspaceId: number,
    signal: AbortSignal | undefined = undefined,
  ): Promise<GetQuestionnairesResponse> {
    const response = await api.get<GetQuestionnairesResponse>(
      `/module_workspaces/${moduleWorkspaceId}/questionnaires`,
      {
        signal,
      },
    );

    return response.data;
  },
  // GET /api/react/questionnaires/:id
  async get(id: number): Promise<QuestionnaireRead> {
    const response = await api.get<GetQuestionnaireResponse>(
      `/questionnaires/${id}`,
    );

    return response.data.questionnaire;
  },

  // PUT /api/react/questionnaires/:id
  async edit(
    id: number,
    questionnaire: QuestionnaireRequest,
  ): Promise<QuestionnaireRead> {
    const response = await api.put<
      QuestionnaireRequest,
      AxiosResponse<GetQuestionnaireResponse>
    >(
      // maybe get API_URL into api imported above
      `/questionnaires/${id}`,
      questionnaire,
    );

    return response.data.questionnaire;
  },

  // PUT /api/react/questionnaires/:id
  async updateGroupAssignments(
    id: number,
    questionGroupId: number,
    assignments: Assignment[],
  ) {
    const payload = {
      questionnaire: {
        id,
        question_groups_attributes: {
          id: questionGroupId,
          assignments_attributes: assignments,
        },
      },
    };
    const result = await api.put<
      AssigneeUpdateRequest,
      AxiosResponse<GetQuestionnaireResponse>
    >(`/questionnaires/${id}`, payload);
    return result.data.questionnaire;
  },

  // PUT /api/react/questionnaires/:id
  async updateQuestionAssignments(
    id: number,
    questionId: number,
    assignments: Assignment[],
  ) {
    const payload = {
      questionnaire: {
        id,
        questions_attributes: {
          id: questionId,
          assignments_attributes: assignments,
        },
      },
    };
    const result = await api.put<
      AssigneeUpdateRequest,
      AxiosResponse<GetQuestionnaireResponse>
    >(`/questionnaires/${id}`, payload);
    return result.data.questionnaire;
  },

  async updateQuestionResponders(
    id: number,
    questionId: number,
    assignments: Assignment[],
    isQuestion: boolean,
  ) {
    const generatePayload = () => {
      if (isQuestion) {
        return {
          questionnaire: {
            id,
            questions_attributes: {
              id: questionId,
              assignments_attributes: assignments,
            },
          },
        };
      }

      return {
        questionnaire: {
          id,
          question_groups_attributes: {
            id: questionId,
            assignments_attributes: assignments,
          },
        },
      };
    };
    const result = await api.post<
      AssigneeUpdateRequest,
      AxiosResponse<GetQuestionnaireFormReadReponse>
    >(`/questionnaires/${id}/set_responders`, generatePayload());
    return result.data.questionnaire;
  },

  // POST /api/react/module_workspaces/:module_workspace_id/questionnaires
  async create(
    moduleWorkspaceId: number,
    request: QuestionnaireRequest,
  ): Promise<QuestionnaireRead> {
    const response: AxiosResponse<GetQuestionnaireResponse> = await api.post(
      `/module_workspaces/${moduleWorkspaceId}/questionnaires`,
      request,
    );

    return response.data.questionnaire;
  },

  delete(questionnaireId: number) {
    return api.delete(`/questionnaires/${questionnaireId}`);
  },

  async setInReview(questionnaireId: number): Promise<QuestionnaireRead> {
    const response: AxiosResponse<{ questionnaire: QuestionnaireRead }> =
      await api.post(`/questionnaires/${questionnaireId}/reviewing`);
    return response.data.questionnaire;
  },

  async finalize(questionnaireId: number): Promise<QuestionnaireRead> {
    const response: AxiosResponse<{ questionnaire: QuestionnaireRead }> =
      await api.post(`/questionnaires/${questionnaireId}/finalize`);
    return response.data.questionnaire;
  },

  // POST /questionnaires/:questionnaire_id/complete
  async complete(questionnaireId: number): Promise<QuestionnaireRead> {
    const response: AxiosResponse<{ questionnaire: QuestionnaireRead }> =
      await api.post(`/questionnaires/${questionnaireId}/complete`);
    return response.data.questionnaire;
  },

  // GET /api/react/module_workspaces/:module_workspace_id/questionnaires/export_template
  async downloadTemplate(moduleWorkspaceId: number) {
    const response = await api.get(
      `/module_workspaces/${moduleWorkspaceId}/questionnaires/export_template`,
    );
    const blob = new Blob([new Uint8Array(response.data)], {
      type: "application/octet-stream",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "questionnaire_template.xlsx";
    link.click();
  },

  // POST /api/react/module_workspaces/:module_workspace_id/questionnaires/import_template
  async importQuestionnaireTemplate(
    moduleWorkspaceId: number,
    file: File,
    risk_methodology_id: number,
    isPublic: boolean = false,
  ) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("risk_methodology_id", risk_methodology_id.toString());
    formData.append("is_public", isPublic.toString());
    const result = (await api.post(
      `/module_workspaces/${moduleWorkspaceId}/questionnaires/import_template`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
      // @ts-expect-error TS(7008) FIXME: Member 'questionnaire' implicitly has an 'any' typ... Remove this comment to see the full error message
    )) as { data: { questionnaire } };
    return result.data.questionnaire;
  },

  // POST /api/react/questionnaires/:id/import
  async importQuestionnaire(questionnaireId: number, file: File) {
    const formData = new FormData();
    formData.append("file", file);
    const result = (await api.post(
      `/questionnaires/${questionnaireId}/import`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
      // @ts-expect-error TS(7008) FIXME: Member 'questionnaire' implicitly has an 'any' typ... Remove this comment to see the full error message
    )) as { data: { questionnaire } };
    return result.data.questionnaire;
  },

  // GET /api/react/questionnaires/:id/export
  async downloadQuestionnaireTemplate(questionnaireId: number) {
    const response = await api.get(`/questionnaires/${questionnaireId}/export`);
    const blob = new Blob([new Uint8Array(response.data)], {
      type: "application/octet-stream",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "questionnaire_template_export.xlsx";
    link.click();
  },

  sendQuestionnaireToCW(
    moduleWorkspaceID: number,
    questionnaireID: number,
    workspaceIDs: number[],
  ): Promise<void> {
    return api.post(
      `/module_workspaces/${moduleWorkspaceID}/questionnaires/send_to_cw`,
      { questionnaire_id: questionnaireID, workspace_ids: workspaceIDs },
    );
  },

  async saveAnswers(
    questionnaireID: number,
    answeredQuestions: FormQuestionUpsert[],
    isComplete = false,
  ) {
    const response = await api.post<
      QuestionnaireAnswerPostRequest,
      AxiosResponse<GetQuestionnaireResponse>
    >(`/questionnaires/${questionnaireID}/submit_answers`, {
      completed: isComplete,
      questionnaire: {
        questions_attributes: answeredQuestions,
      },
    });

    return response.data.questionnaire;
  },
};
